import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { sessionApi, restaurantApi, preferencesApi } from '../services/api';
import { useSession } from '../context/SessionContext';
import { useAuth } from '../context/AuthContext';
import UnifiedFilters, { FilterState } from '../components/UnifiedFilters';
import { City } from '../types';
import { saveSession } from '../utils/sessionHistory';
import Button from '../components/Button';
import BackButton from '../components/BackButton';
import PageTransition from '../components/PageTransition';
import SessionProgress from '../components/SessionProgress';
import ErrorDisplay from '../components/ui/ErrorDisplay';

const CreateSessionPage: React.FC = () => {
  const navigate = useNavigate();
  const locationState = useLocation().state as { restaurantIds?: string[]; citySlug?: string } | null;
  const { setParticipantId } = useSession();
  const { user, isAuthenticated } = useAuth();

  // Manual selection mode (from catalog)
  const restaurantIds = locationState?.restaurantIds;
  const isManualMode = restaurantIds && restaurantIds.length > 0;

  const [nickname, setNickname] = useState('');
  const [cities, setCities] = useState<City[]>([]);
  const [selectedCity, setSelectedCity] = useState(locationState?.citySlug || 'tomsk');
  const [filterState, setFilterState] = useState<FilterState>({
    rubrics: [],
    cuisineTags: [],
    menuTags: [],
    features: [],
  });
  const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [maxRestaurants, setMaxRestaurants] = useState(20);
  const [prefsApplied, setPrefsApplied] = useState(false);

  useEffect(() => {
    restaurantApi.getCities().then((data) => {
      setCities(data.cities);
      if (!locationState?.citySlug && data.cities.length > 0) {
        setSelectedCity(data.cities[0].slug);
      }
    }).catch(() => {});
  }, [locationState?.citySlug]);

  const handleGeoRequest = () => {
    if (filterState.geoActive) {
      setFilterState(prev => ({ ...prev, geoActive: false }));
      setLocation(null);
      return;
    }
    if (!navigator.geolocation) {
      setError('Геолокация не поддерживается браузером');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocation({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
        setFilterState(prev => ({ ...prev, geoActive: true }));
        setError('');
      },
      () => {
        setError('Не удалось определить местоположение');
      }
    );
  };

  const handleCreate = async () => {
    const creatorName = isAuthenticated && user?.name
      ? user.name
      : nickname.trim();

    if (!creatorName) {
      setError('Введите ваше имя');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const data = await sessionApi.create({
        citySlug: selectedCity,
        creatorNickname: creatorName,
        restaurantIds: isManualMode ? restaurantIds : undefined,
        maxRestaurants: isManualMode ? undefined : maxRestaurants,
        filters: isManualMode ? undefined : {
          radius: filterState.geoActive ? (filterState.radius || 1000) : undefined,
          cuisineTypes: filterState.rubrics.length > 0 ? filterState.rubrics : undefined,
          cuisineTags: filterState.cuisineTags.length > 0 ? filterState.cuisineTags : undefined,
          menuTags: filterState.menuTags.length > 0 ? filterState.menuTags : undefined,
          features: (filterState.features || []).length > 0 ? filterState.features : undefined,
        },
        location: filterState.geoActive && location ? location : undefined,
      });

      setParticipantId(data.participantId);
      localStorage.setItem(`session_${data.sessionId}_pid`, data.participantId);
      localStorage.setItem(`session_${data.sessionId}_link`, data.shareLink);
      saveSession(data.sessionId);
      navigate(`/session/${data.sessionId}/lobby`);
    } catch (err) {
      setError('Ошибка при создании сессии. Попробуйте снова.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageTransition className="min-h-screen bg-gradient-to-br from-orange-50 to-red-50 px-4 py-8">
      <div className="max-w-md mx-auto">
        <div className="flex items-center gap-3 mb-5">
          <BackButton onClick={() => navigate('/')} />
          <h1 className="text-2xl font-bold text-gray-900">Новая сессия</h1>
        </div>
        <SessionProgress step={1} />
        <p className="text-sm text-gray-500 mb-6">
          {isManualMode
            ? 'Сессия с выбранными заведениями'
            : 'Мы подберём заведения по вашим предпочтениям'
          }
        </p>

        <div className="space-y-5">
          {/* Manual selection info */}
          {isManualMode && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-orange-600">{restaurantIds.length}</span>
              </div>
              <div>
                <p className="text-sm text-orange-800 font-medium">
                  Выбрано заведений
                </p>
                <p className="text-xs text-orange-600 mt-0.5">
                  Город: {cities.find(c => c.slug === selectedCity)?.name || selectedCity}
                </p>
              </div>
            </div>
          )}

          {/* Nickname — скрываем если авторизован и есть имя */}
          {isAuthenticated && user?.name ? (
            <div className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 p-4">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-red-400 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">
                  {user.name[0].toUpperCase()}
                </span>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{user.name}</p>
                <p className="text-xs text-gray-400">Создатель сессии</p>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                Ваше имя
              </label>
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="Как вас зовут?"
                maxLength={30}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-orange-200 focus:border-orange-400 outline-none text-sm transition-all"
              />
            </div>
          )}


          {/* Filters — only in auto mode */}
          {!isManualMode && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Предпочтения
                </label>
                {isAuthenticated && user?.preferencesConfigured && (
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        const data = await preferencesApi.get();
                        setFilterState((prev) => ({
                          ...prev,
                          cuisineTags: data.likedCuisines,
                          menuTags: data.likedMenuTags,
                        }));
                        setPrefsApplied(true);
                      } catch {/* ignore */}
                    }}
                    className={`flex items-center gap-1 text-xs font-medium transition-colors ${
                      prefsApplied ? 'text-orange-400' : 'text-orange-500 hover:text-orange-600'
                    }`}
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" stroke="none">
                      <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
                    </svg>
                    {prefsApplied ? 'Применено' : 'Мои предпочтения'}
                  </button>
                )}
              </div>
              <UnifiedFilters
                filters={filterState}
                onChange={setFilterState}
                showGeo
                onGeoRequest={handleGeoRequest}
              />
            </div>
          )}

          {/* Max restaurants slider — only in auto mode */}
          {!isManualMode && (
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Количество заведений
                </label>
                <span className="text-sm font-bold text-orange-500">{maxRestaurants}</span>
              </div>
              <input
                type="range"
                min={5}
                max={100}
                step={5}
                value={maxRestaurants}
                onChange={(e) => setMaxRestaurants(Number(e.target.value))}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-orange-500"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1.5">
                <span>5</span>
                <span>50</span>
                <span>100</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">
                Из всех подходящих заведений при старте будут выбраны топ {maxRestaurants} с учётом предпочтений участников
              </p>
            </div>
          )}

          {/* Error */}
          <ErrorDisplay error={error} />

          {/* Create Button */}
          <Button size="lg" fullWidth disabled={loading} onClick={handleCreate}>
            {loading ? 'Создаём...' : 'Создать сессию'}
          </Button>
        </div>
      </div>
    </PageTransition>
  );
};

export default CreateSessionPage;
